import { useSyncExternalStore } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import {
  ChangePasswordPage,
  ForgotPasswordPage,
  LoginPage,
  ResetPasswordPage,
  SignupPage,
} from "../auth/AuthPage";
import { SessionBoundary } from "../auth/SessionBoundary";
import { AccountDeletionPage } from "../auth/AccountDeletionPage";
import { BillingLandingPage } from "../billing-onboarding/BillingLandingPage";
import { MarketingHome } from "../marketing/MarketingHome";
import { AnalyticsConsent } from "../marketing/AnalyticsConsent";
import { InstagramCommentsGuide } from "../marketing/InstagramCommentsGuide";
import { PublicGuidePage, publicGuides } from "../marketing/PublicGuidePage";
import { SolutionPage } from "../marketing/SolutionPage";
import { SocialCommercePage } from "../marketing/SocialCommercePage";
import { PublicQuotationPage } from "../quotations/PublicQuotationPage";
import { AuthenticatedSupportChat, PublicSupportLayout } from "../support-chat/SupportChatMount";
import { AppRoutes } from "./router";

function routerBasename(): string {
  const guidePaths = Object.values(publicGuides).flatMap(({ metadata }) => [metadata.path, metadata.path.replace(/\/$/, "")]);
  if (
    window.location.pathname === "/"
    || [...guidePaths, "/instagram-crm", "/instagram-crm/", "/instagram-crm-india", "/instagram-crm-india/", "/instagram-comment-auto-reply", "/instagram-comment-auto-reply/", "/facebook-lead-crm", "/facebook-lead-crm/", "/whatsapp-crm", "/whatsapp-crm/", "/social-commerce", "/social-commerce/"].includes(window.location.pathname)
  ) return "/";
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return base || "/";
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<PublicSupportLayout />}>
        <Route path="/" element={<MarketingHome />} />
        <Route path={publicGuides.instagram.metadata.path} element={<InstagramCommentsGuide />} />
        <Route path={publicGuides.instagramAutoReplyTools.metadata.path} element={<PublicGuidePage kind="instagramAutoReplyTools" />} />
        <Route path={publicGuides.facebook.metadata.path} element={<PublicGuidePage kind="facebook" />} />
        <Route path={publicGuides.facebookAutoReplyTools.metadata.path} element={<PublicGuidePage kind="facebookAutoReplyTools" />} />
        <Route path={publicGuides.whatsapp.metadata.path} element={<PublicGuidePage kind="whatsapp" />} />
        <Route path={publicGuides.whatsappAutoReplyTools.metadata.path} element={<PublicGuidePage kind="whatsappAutoReplyTools" />} />
        <Route path="instagram-crm" element={<SolutionPage slug="instagram-crm" />} />
        <Route path="instagram-crm-india" element={<SolutionPage slug="instagram-crm-india" />} />
        <Route path="instagram-comment-auto-reply" element={<SolutionPage slug="instagram-comment-auto-reply" />} />
        <Route path="facebook-lead-crm" element={<SolutionPage slug="facebook-lead-crm" />} />
        <Route path="whatsapp-crm" element={<SolutionPage slug="whatsapp-crm" />} />
        <Route path="social-commerce" element={<SocialCommercePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignupPage />} />
        <Route path="billing" element={<BillingLandingPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="account-deletion" element={<AccountDeletionPage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
        <Route path="quote/:shareToken" element={<PublicQuotationPage />} />
      </Route>
      <Route path="*" element={<SessionBoundary><AppRoutes /><AuthenticatedSupportChat /></SessionBoundary>} />
    </>,
  ),
  {
    basename: routerBasename(),
    future: { v7_relativeSplatPath: true },
  },
);

export function App() {
  const pathname = useSyncExternalStore(router.subscribe, () => router.state.location.pathname);
  return <><RouterProvider router={router} /><AnalyticsConsent pathname={pathname} /></>;
}
